import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import { ACCESS_LEVELS, FILE_TYPE_CONFIG } from './dataRoomData';

function PDFPreview({ doc, currentPage }) {
  return (
    <div className="flex flex-col gap-4 p-6 bg-white rounded-lg shadow-inner border border-gray-200 min-h-96">
      {/* Page Header */}
      <div className="border-b border-gray-200 pb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="h-3 bg-gray-800 rounded" style={{ width: '60%' }} />
          <span className="text-xs text-gray-400 font-caption">Page {currentPage}</span>
        </div>
        <div className="h-2 bg-gray-300 rounded w-40" />
      </div>
      {/* Executive Summary */}
      <div>
        <div className="h-2.5 bg-gray-700 rounded w-48 mb-3" />
        <div className="space-y-1.5">
          {[90, 85, 92, 78, 88, 70]?.map((w, i) => (
            <div key={i} className="h-1.5 bg-gray-200 rounded" style={{ width: `${w}%` }} />
          ))}
        </div>
      </div>
      {/* Financial Table */}
      <div className="border border-gray-200 rounded overflow-hidden">
        <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
          <div className="h-2 bg-gray-500 rounded w-48" />
        </div>
        {[['Project IRR', '13.4%', '12.8%', '11.9%'], ['Equity Multiple', '1.62x', '1.54x', '1.43x'], ['NPV (£m)', '42.3', '38.1', '31.7'], ['Payback Period', '7.2 yrs', '7.8 yrs', '8.5 yrs']]?.map(([label, base, low, stress], i) => (
          <div key={i} className={`flex px-3 py-2 text-xs font-caption ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
            <span className="flex-1 text-gray-600 font-medium">{label}</span>
            <span className="w-16 text-center text-gray-800 font-semibold">{base}</span>
            <span className="w-16 text-center text-gray-500">{low}</span>
            <span className="w-16 text-center text-gray-400">{stress}</span>
          </div>
        ))}
      </div>
      {/* Body Text */}
      <div className="space-y-3">
        <div>
          <div className="h-2 bg-gray-600 rounded w-56 mb-2" />
          <div className="space-y-1.5">
            {[88, 95, 82, 90, 75]?.map((w, i) => (
              <div key={i} className="h-1.5 bg-gray-200 rounded" style={{ width: `${w}%` }} />
            ))}
          </div>
        </div>
        <div>
          <div className="h-2 bg-gray-600 rounded w-44 mb-2" />
          <div className="space-y-1.5">
            {[92, 80, 88, 76, 85, 70]?.map((w, i) => (
              <div key={i} className="h-1.5 bg-gray-200 rounded" style={{ width: `${w}%` }} />
            ))}
          </div>
        </div>
      </div>
      {/* Figure */}
      <div className="border border-gray-200 rounded p-3 bg-gray-50">
        <div className="flex items-end gap-2 h-20 mb-2">
          {[60, 75, 55, 85, 70, 90, 65, 80]?.map((h, i) => (
            <div key={i} className="flex-1 rounded-t" style={{ height: `${h}%`, background: i % 2 === 0 ? '#3b4c4c' : '#3b82f6' }} />
          ))}
        </div>
        <div className="h-1.5 bg-gray-300 rounded w-64 mx-auto" />
      </div>
    </div>
  );
}

function XLSXPreview({ doc }) {
  const headers = ['Period', 'Revenue (£m)', 'OPEX (£m)', 'EBITDA (£m)', 'Capex (£m)', 'Free Cash Flow (£m)', 'IRR (%)'];
  const rows = [
    ['2025', '18.4', '6.2', '12.2', '3.1', '9.1', '—'],
    ['2026', '22.1', '6.8', '15.3', '2.8', '12.5', '—'],
    ['2027', '25.6', '7.1', '18.5', '2.5', '16.0', '—'],
    ['2028', '28.3', '7.4', '20.9', '2.2', '18.7', '—'],
    ['2029', '30.1', '7.6', '22.5', '2.0', '20.5', '13.4%'],
    ['2030', '31.8', '7.9', '23.9', '1.8', '22.1', '—'],
  ];
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-auto">
      <div className="bg-green-50 px-4 py-2 border-b border-gray-200">
        <span className="text-xs font-caption font-semibold text-green-700">Financial Model — Base Case Projections</span>
      </div>
      <table className="w-full text-xs font-caption">
        <thead>
          <tr className="bg-gray-100">
            {headers?.map((h, i) => (
              <th key={i} className="px-3 py-2 text-left font-semibold text-gray-600 border-r border-gray-200 last:border-r-0 whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows?.map((row, i) => (
            <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              {row?.map((cell, j) => (
                <td key={j} className={`px-3 py-2 border-r border-gray-200 last:border-r-0 ${j === 0 ? 'font-semibold text-gray-700' : 'text-gray-600'} ${j === row?.length - 1 && cell !== '—' ? 'font-bold text-green-700' : ''}`}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DOCXPreview({ doc }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-8 min-h-96 font-caption">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <div className="h-4 bg-gray-800 rounded w-3/4 mx-auto mb-2" />
          <div className="h-2.5 bg-gray-400 rounded w-1/2 mx-auto mb-1" />
          <div className="h-2 bg-gray-300 rounded w-1/3 mx-auto" />
        </div>
        <div className="border-t-2 border-gray-800 pt-4 mb-4">
          <div className="h-2.5 bg-gray-700 rounded w-40 mb-3" />
          <div className="space-y-1.5">
            {[95, 88, 92, 85, 90, 78, 88]?.map((w, i) => (
              <div key={i} className="h-1.5 bg-gray-200 rounded" style={{ width: `${w}%` }} />
            ))}
          </div>
        </div>
        <div className="mb-4">
          <div className="h-2.5 bg-gray-700 rounded w-48 mb-3" />
          <div className="pl-4 space-y-2">
            {['Clause 1: Definitions and Interpretation', 'Clause 2: Representations and Warranties', 'Clause 3: Conditions Precedent', 'Clause 4: Obligations of the Parties']?.map((clause, i) => (
              <div key={i} className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-1.5 flex-shrink-0" />
                <div className="h-1.5 bg-gray-200 rounded flex-1" style={{ width: '80%' }} />
              </div>
            ))}
          </div>
        </div>
        <div className="border border-gray-200 rounded p-3 bg-gray-50">
          <div className="h-2 bg-gray-500 rounded w-32 mb-2" />
          <div className="space-y-1">
            {[85, 90, 78]?.map((w, i) => (
              <div key={i} className="h-1.5 bg-gray-200 rounded" style={{ width: `${w}%` }} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DocumentPreviewModal({ doc, userRole, onClose, onDownload, onViewHistory, onManageAccess, onToast }) {
  const [currentPage, setCurrentPage] = useState(1);
  const fileConfig = FILE_TYPE_CONFIG?.[doc?.fileType] || FILE_TYPE_CONFIG?.pdf;
  const accessConfig = ACCESS_LEVELS?.[doc?.accessLevel] || ACCESS_LEVELS?.internal;
  const totalPages = doc?.fileType === 'pdf' ? (doc?.pages || 1) : 1;
  const canManageAccess = userRole === 'admin';

  const handleShare = () => {
    onToast({ type: 'success', title: 'Link copied', message: 'Document link copied to clipboard.' });
  };

  return (
    <div
      className="fixed inset-0 flex items-stretch"
      style={{ zIndex: 'var(--z-modal, 1000)', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
    >
      <div className="flex flex-col flex-1 bg-background m-4 rounded-2xl overflow-hidden shadow-2xl">
        {/* Modal Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-border bg-card flex-shrink-0">
          <div
            className="flex items-center justify-center w-9 h-9 rounded-lg flex-shrink-0"
            style={{ background: fileConfig?.bg }}
          >
            <Icon name={fileConfig?.icon} size={18} color={fileConfig?.color} strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-heading text-base font-semibold text-foreground truncate">{doc?.name}</h2>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs px-2 py-0.5 rounded-md bg-primary/10 text-primary font-caption font-semibold">{doc?.version}</span>
              <span className="text-xs px-2 py-0.5 rounded-md bg-muted text-muted-foreground font-caption capitalize">{doc?.category?.replace('-', ' ')}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-9 h-9 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-200"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex flex-1 overflow-hidden">
          {/* Preview Area */}
          <div className="flex-1 overflow-y-auto p-6 bg-muted/30">
            {doc?.fileType === 'pdf' && <PDFPreview doc={doc} currentPage={currentPage} />}
            {doc?.fileType === 'xlsx' && <XLSXPreview doc={doc} />}
            {doc?.fileType === 'docx' && <DOCXPreview doc={doc} />}

            {/* Page Navigator */}
            {doc?.fileType === 'pdf' && totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-4">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-caption font-medium border border-border text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
                >
                  <Icon name="ChevronLeft" size={14} /> Prev
                </button>
                <span className="text-sm font-caption text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-caption font-medium border border-border text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
                >
                  Next <Icon name="ChevronRight" size={14} />
                </button>
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <div className="w-72 flex-shrink-0 border-l border-border bg-card overflow-y-auto">
            <div className="p-5 space-y-5">
              {/* Metadata */}
              <div>
                <h3 className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Document Details</h3>
                <div className="space-y-2.5">
                  {[
                    { label: 'Version', value: doc?.version },
                    { label: 'File Size', value: doc?.fileSize },
                    { label: 'Uploaded by', value: doc?.modifiedBy },
                    { label: 'Last Modified', value: doc?.modifiedDate },
                    { label: 'File Type', value: doc?.fileType?.toUpperCase() },
                  ]?.map(({ label, value }) => (
                    <div key={label} className="flex justify-between items-start gap-2">
                      <span className="text-xs font-caption text-muted-foreground flex-shrink-0">{label}</span>
                      <span className="text-xs font-caption text-foreground font-medium text-right">{value}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-xs font-caption text-muted-foreground">Access Level</span>
                    <span
                      className="text-xs font-semibold px-2 py-0.5 rounded-full font-caption border"
                      style={{ color: accessConfig?.color, background: accessConfig?.bg, borderColor: accessConfig?.border }}
                    >
                      {accessConfig?.label}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-2">
                <button
                  onClick={() => onDownload(doc)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-caption font-semibold bg-primary text-white hover:bg-primary/90 transition-all duration-200"
                >
                  <Icon name="Download" size={15} /> Download
                </button>
                <button
                  onClick={() => onViewHistory(doc)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-caption font-medium border border-border text-foreground hover:bg-muted transition-all duration-200"
                >
                  <Icon name="History" size={15} /> Version History
                </button>
                {canManageAccess && (
                  <button
                    onClick={() => onManageAccess(doc)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-caption font-medium border border-border text-foreground hover:bg-muted transition-all duration-200"
                  >
                    <Icon name="Users" size={15} /> Manage Access
                  </button>
                )}
                <button
                  onClick={handleShare}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-caption font-medium border border-border text-foreground hover:bg-muted transition-all duration-200"
                >
                  <Icon name="Link" size={15} /> Share Link
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
